import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

/**
 * Volunteer Recruitment Campaign admin widget.
 * Lists per-email schedule + send status, lets admin:
 *  - Upload a CSV of contacts
 *  - Dry-run preview any email
 *  - Manually fire any email (Email 1 is meant to be manual; others auto on schedule)
 *  - Suppress a contact (unsubscribe)
 */
export default function CampaignCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [sendEmail1OnAdd, setSendEmail1OnAdd] = useState(true);
  const fileRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const r = await api.get("/admin/campaign/status");
      setStatus(r.data);
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 60000);
    return () => clearInterval(t);
  }, []);

  const addContact = async (e) => {
    e?.preventDefault?.();
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      alert("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      const params = new URLSearchParams({ email, name: newName.trim(), phone: newPhone.trim() });
      if (sendEmail1OnAdd) params.set("send_email_index", "1");
      const r = await api.post(`/admin/campaign/add-contact?${params.toString()}`);
      if (r.data.status === "exists") {
        alert(`⚠️ ${email} is already in the campaign list.`);
      } else {
        const sentLine = r.data.email_sent?.sent ? `\n📧 Email 1 sent successfully.` : (sendEmail1OnAdd ? "\n⚠️ Contact added but Email 1 send failed." : "");
        alert(`✅ Added ${email} to campaign${sentLine}`);
        setNewName(""); setNewEmail(""); setNewPhone("");
      }
      await fetchStatus();
    } catch (err) {
      alert(`❌ Failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/campaign/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      alert(`✅ Upload complete\n\nInserted: ${r.data.inserted}\nUpdated: ${r.data.updated}\nSkipped: ${r.data.skipped}\n${r.data.errors?.length ? "Issues:\n" + r.data.errors.join("\n") : ""}`);
      await fetchStatus();
    } catch (e) {
      alert(`❌ Upload failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const previewEmail = async (idx) => {
    const to = window.prompt(`Preview Email ${idx} — send a single test copy to which address?`, "info@itsthefreedominme.com");
    if (!to) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/campaign/send?index=${idx}&dry_run_to=${encodeURIComponent(to)}`);
      alert(`✅ Preview sent to ${to}\n\n${r.data.sent} sent · ${r.data.failed} failed`);
    } catch (e) {
      alert(`❌ Preview failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const sendEmail = async (idx) => {
    if (!window.confirm(`Send Email ${idx} to ALL eligible contacts right now?\n\nEligible: ${status?.per_email?.[idx]?.eligible_now ?? "?"} contacts\n\nThis cannot be undone. (Suppressed contacts are skipped automatically.)`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/campaign/send?index=${idx}`);
      if (r.data.status === "blocked") {
        if (!window.confirm(`Email ${idx} was already sent at ${r.data.last_sent_at}.\n\nClick OK to FORCE re-send anyway.`)) {
          setBusy(false);
          return;
        }
        const r2 = await api.post(`/admin/campaign/send?index=${idx}&force=true`);
        alert(`✅ Force-sent Email ${idx}\n\n${r2.data.sent} sent · ${r2.data.failed} failed`);
      } else {
        alert(`✅ Email ${idx} sent\n\n${r.data.sent} sent · ${r.data.failed} failed`);
      }
      await fetchStatus();
    } catch (e) {
      alert(`❌ Send failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const suppress = async () => {
    const email = window.prompt("Suppress (stop campaign emails to) which email address?");
    if (!email) return;
    try {
      const r = await api.post(`/admin/campaign/suppress?email=${encodeURIComponent(email)}`);
      alert(r.data.suppressed ? `✅ ${email} suppressed` : `⚠️ No contact found for ${email}`);
      await fetchStatus();
    } catch (e) {
      alert(`❌ Failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  if (!status) return null;

  const emailOrder = [1, 2, 3, 4, 5, 6];

  return (
    <div data-testid="campaign-card" className="rounded-2xl border border-rose-100 bg-white shadow-sm mb-6 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-rose-50 bg-gradient-to-r from-rose-50/60 via-white to-amber-50/40">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold font-[Outfit] px-2.5 py-1 rounded-full border bg-rose-100 text-rose-800 border-rose-300">
            <span className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" />
            6-Email Volunteer Recruitment
          </span>
          <span className="text-xs text-emerald-900/70 font-[Outfit] tabular-nums">
            {status.active_contacts} active · {status.suppressed_contacts} suppressed (signed up)
          </span>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => upload(e.target.files?.[0])}
            className="hidden"
            data-testid="campaign-csv-input"
          />
          <Button
            data-testid="campaign-add-one-btn"
            onClick={() => setAddOpen((v) => !v)}
            disabled={busy}
            className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-[Outfit] h-8 text-xs"
          >
            + Add Contact
          </Button>
          <Button
            data-testid="campaign-upload-btn"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            variant="outline"
            className="rounded-full border-rose-300 text-rose-700 hover:bg-rose-50 font-[Outfit] h-8 text-xs"
          >
            ↑ Upload CSV
          </Button>
          <Button
            data-testid="campaign-suppress-btn"
            onClick={suppress}
            variant="outline"
            className="rounded-full border-slate-300 text-slate-700 hover:bg-slate-50 font-[Outfit] h-8 text-xs"
          >
            Suppress Email
          </Button>
        </div>
      </div>

      {/* Add Contact inline form — collapsible */}
      {addOpen && (
        <form
          onSubmit={addContact}
          data-testid="campaign-add-form"
          className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_120px_auto] gap-2 items-center px-5 py-3 bg-emerald-50/60 border-b border-emerald-100"
        >
          <input
            type="email"
            required
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="email@example.com *"
            data-testid="campaign-add-email"
            className="rounded-lg border border-emerald-300 px-3 py-2 text-sm font-[Outfit] bg-white focus:outline-none focus:border-emerald-500"
          />
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Name (optional)"
            data-testid="campaign-add-name"
            className="rounded-lg border border-emerald-300 px-3 py-2 text-sm font-[Outfit] bg-white focus:outline-none focus:border-emerald-500"
          />
          <input
            type="tel"
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
            placeholder="Phone (optional)"
            data-testid="campaign-add-phone"
            className="rounded-lg border border-emerald-300 px-3 py-2 text-sm font-[Outfit] bg-white focus:outline-none focus:border-emerald-500"
          />
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-xs font-[Outfit] text-emerald-800 cursor-pointer whitespace-nowrap">
              <input
                type="checkbox"
                checked={sendEmail1OnAdd}
                onChange={(e) => setSendEmail1OnAdd(e.target.checked)}
                data-testid="campaign-add-send-flag"
                className="accent-emerald-700"
              />
              Send Email 1 now
            </label>
            <Button
              type="submit"
              disabled={busy}
              data-testid="campaign-add-submit"
              className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-[Outfit] h-8 text-xs"
            >
              {busy ? "Adding…" : "Add"}
            </Button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-6 divide-x divide-rose-50 border-b border-rose-50">
        {emailOrder.map((idx) => {
          const meta = status.per_email[idx] || {};
          const sent = !!meta.last_sent_at;
          const sched = meta.scheduled || {};
          const dt = sched.date ? new Date(`${sched.date}T${String(sched.hour_utc).padStart(2,"0")}:00:00Z`) : null;
          const dtLabel = dt ? dt.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", hour12: true }) + " ET" : "Manual";
          return (
            <div key={idx} data-testid={`campaign-email-${idx}`} className={`p-3 ${sent ? "bg-emerald-50/40" : "bg-white"}`}>
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-[Outfit] text-xs font-bold text-emerald-900">Email {idx}</div>
                <div className={`text-[10px] uppercase tracking-wider font-[Outfit] font-bold ${sent ? "text-emerald-700" : "text-slate-500"}`}>
                  {sent ? "Sent" : "Pending"}
                </div>
              </div>
              <div className="text-[11px] text-slate-600 mt-0.5 font-[Outfit]">{dtLabel}</div>
              <div className="text-[11px] text-emerald-900/70 mt-1 font-[Outfit] tabular-nums">
                {sent ? `${meta.sent_count} sent · ${meta.failed_count} failed` : `${meta.eligible_now} eligible`}
              </div>
              <div className="flex gap-1.5 mt-2">
                <button
                  data-testid={`campaign-preview-${idx}`}
                  onClick={() => previewEmail(idx)}
                  disabled={busy}
                  className="text-[10px] font-[Outfit] font-semibold text-emerald-700 hover:text-emerald-900 disabled:text-slate-300"
                >
                  Preview
                </button>
                <button
                  data-testid={`campaign-send-${idx}`}
                  onClick={() => sendEmail(idx)}
                  disabled={busy}
                  className="text-[10px] font-[Outfit] font-semibold text-rose-700 hover:text-rose-900 disabled:text-slate-300"
                >
                  Send Now
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-5 py-2.5 bg-amber-50/40 text-[11px] text-amber-900 font-[Outfit]">
        💡 CSV columns: <span className="font-mono bg-white px-1 py-0.5 rounded border border-amber-200">name, email, phone, alumni_year, tags</span>. Volunteers who sign up via the form are auto-suppressed.
      </div>
    </div>
  );
}
